// src/components/auth/MFAEnrollment.jsx
// Component for enrolling TOTP MFA - shows QR code and verifies setup

import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Shield, Loader2, CheckCircle2, AlertTriangle, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

export default function MFAEnrollment({ isOpen, onEnrolled, onCancelled, onDefer, allowSkip = false, showSkipButton = false }) {
  const [factorId, setFactorId] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [showSecret, setShowSecret] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Reset state when dialog opens
      setFactorId('');
      setQrCode('');
      setSecret('');
      setVerifyCode('');
      setError('');
      setShowSecret(false);
      // Clean up any incomplete factors before starting new enrollment
      cleanupIncompleteFactors().then(() => {
        enrollFactor();
      });
    }
  }, [isOpen]);

  const cleanupIncompleteFactors = async () => {
    try {
      const { data: factorsData, error: listError } = await supabase.auth.mfa.listFactors();
      if (listError) {
        logger.debug('Could not list factors for cleanup:', listError);
        return;
      }

      // Find unverified factors and remove them
      const unverifiedFactors = factorsData?.totp?.filter(f => f.status !== 'verified') || [];
      for (const factor of unverifiedFactors) {
        try {
          await supabase.auth.mfa.unenroll({ factorId: factor.id });
          logger.debug('Cleaned up incomplete factor:', factor.id);
        } catch (unenrollError) {
          logger.debug('Could not unenroll incomplete factor:', unenrollError);
        }
      }
    } catch (err) {
      logger.debug('Error during factor cleanup:', err);
      // Don't block enrollment if cleanup fails
    }
  };

  const enrollFactor = async () => {
    setLoading(true);
    setError('');

    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Authenticator App'
      });

      if (error) {
        throw error;
      }

      logger.debug('MFA enrollment started:', { factorId: data.id });
      setFactorId(data.id);
      setQrCode(data.totp.qr_code);
      setSecret(data.totp.secret);
    } catch (err) {
      logger.error('MFA enrollment error:', err);
      setError('Nie udało się rozpocząć konfiguracji MFA. Spróbuj ponownie.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (verifyCode.length !== 6) {
      setError('Kod musi mieć 6 cyfr');
      return;
    }

    setVerifying(true);
    setError('');

    try {
      // Create challenge
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId
      });

      if (challengeError) {
        throw challengeError;
      }

      // Verify the code
      const { data: verifyData, error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: verifyCode
      });

      if (verifyError) {
        throw verifyError;
      }

      logger.debug('MFA enrollment verified successfully');
      toast.success('Uwierzytelnianie dwuskładnikowe zostało włączone!');
      onEnrolled();
    } catch (err) {
      logger.error('MFA verification error:', err);
      if (err.message?.includes('Invalid')) {
        setError('Nieprawidłowy kod. Upewnij się, że wpisujesz aktualny kod z aplikacji.');
      } else {
        setError('Weryfikacja nie powiodła się. Spróbuj ponownie.');
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleCancel = async () => {
    // If we started enrollment but didn't complete, unenroll the factor
    if (factorId) {
      try {
        await supabase.auth.mfa.unenroll({ factorId });
      } catch (err) {
        logger.debug('Could not unenroll incomplete factor:', err);
      }
    }
    onCancelled();
  };

  const handleDefer = async () => {
    // If we started enrollment but didn't complete, unenroll the factor
    if (factorId) {
      try {
        await supabase.auth.mfa.unenroll({ factorId });
      } catch (err) {
        logger.debug('Could not unenroll incomplete factor:', err);
      }
    }
    // Call defer callback to disable MFA toggle
    if (onDefer) {
      onDefer();
    } else {
      // Fallback to regular cancel if defer callback not provided
      onCancelled();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent 
        className="sm:max-w-md max-h-[90vh] overflow-y-auto" 
        hideCloseButton
        overlayClassName="backdrop-blur-md transition-all duration-500 ease-in-out"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="text-center sm:text-center">
          <div className="mx-auto mb-4">
            <div className="h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center">
              <Shield className="h-8 w-8 text-blue-600" />
            </div>
          </div>
          <DialogTitle className="text-xl">
            Konfiguracja uwierzytelniania dwuskładnikowego
          </DialogTitle>
          <DialogDescription className="text-center pt-2">
            {allowSkip 
              ? 'Jako administrator musisz skonfigurować co najmniej jedną metodę bezpiecznego logowania: uwierzytelnianie dwuskładnikowe (2FA) lub logowanie biometryczne.'
              : 'Jako administrator musisz skonfigurować uwierzytelnianie dwuskładnikowe (2FA) dla zwiększenia bezpieczeństwa konta.'
            }
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-4" />
            <p className="text-sm text-slate-500">Przygotowywanie konfiguracji...</p>
          </div>
        ) : error && !qrCode ? (
          <div className="flex flex-col items-center justify-center py-8">
            <AlertTriangle className="h-8 w-8 text-red-500 mb-4" />
            <p className="text-sm text-red-600 text-center">{error}</p>
            <Button onClick={enrollFactor} variant="outline" className="mt-4">
              Spróbuj ponownie
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Step 1: Install app */}
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                  1
                </div>
                <div>
                  <p className="font-medium text-slate-900">Zainstaluj aplikację authenticator</p>
                  <p className="text-sm text-slate-500 mt-1">
                    Pobierz Google Authenticator, Microsoft Authenticator lub inną aplikację TOTP na swój telefon.
                  </p>
                </div>
              </div>
            </div>

            {/* Step 2: Scan QR */}
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                  2
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-900">Zeskanuj kod QR</p>
                  <p className="text-sm text-slate-500 mt-1 mb-3">
                    Otwórz aplikację authenticator i zeskanuj poniższy kod QR.
                  </p>

                  {/* QR Code */}
                  <div className="flex justify-center">
                    <div className="bg-white p-3 rounded-lg border shadow-sm">
                      <img
                        src={qrCode}
                        alt="QR Code do skanowania"
                        className="w-48 h-48"
                      />
                    </div>
                  </div>

                  {/* Manual entry option */}
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => setShowSecret(!showSecret)}
                      className="text-sm text-blue-600 hover:text-blue-700 underline"
                    >
                      {showSecret ? 'Ukryj klucz' : 'Nie możesz zeskanować? Wprowadź ręcznie'}
                    </button>
                    {showSecret && (
                      <div className="mt-2 p-2 bg-slate-100 rounded text-xs font-mono break-all">
                        {secret}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3: Enter code */}
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                  3
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-900">Wprowadź kod weryfikacyjny</p>
                  <p className="text-sm text-slate-500 mt-1 mb-3">
                    Wpisz 6-cyfrowy kod wyświetlony w aplikacji authenticator.
                  </p>

                  <div className="space-y-2">
                    <Input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      placeholder="000000"
                      value={verifyCode}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '');
                        setVerifyCode(val);
                        setError('');
                      }}
                      className="text-center text-2xl tracking-[0.5em] font-mono"
                      autoComplete="one-time-code"
                    />
                    {error && (
                      <p className="text-xs text-red-600 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        {error}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-col gap-2 mt-4">
          <Button
            onClick={handleVerify}
            disabled={loading || verifying || verifyCode.length !== 6}
            className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
          >
            {verifying ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Weryfikacja...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Włącz uwierzytelnianie dwuskładnikowe
              </>
            )}
          </Button>
          {onDefer && (
            <Button
              variant="outline"
              onClick={handleDefer}
              disabled={loading || verifying}
              className="w-full text-slate-600 hover:text-slate-700 hover:bg-slate-50"
            >
              Spróbuję później
            </Button>
          )}
          {showSkipButton && (
            <Button
              variant="ghost"
              onClick={onCancelled}
              disabled={loading || verifying}
              className="w-full text-slate-500 hover:text-slate-700 hover:bg-slate-50"
            >
              Pomiń na razie
            </Button>
          )}
          {allowSkip && !showSkipButton && !onDefer && (
            <Button
              variant="outline"
              onClick={onCancelled}
              disabled={loading || verifying}
              className="w-full"
            >
              Skonfiguruj logowanie biometryczne zamiast tego
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
